import React, { useEffect } from 'react';
import { Stack } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { useData } from '../../../data-layer';
import Localized from '../Localized';
import DraftBadge from '../DraftBadge';
import GenreBadge from '../GenreBadge';
import { VersionedDocumentStatus } from '../../../API';
import { EntitlementsVisibilityBadge } from '../Entitlements';
import { PermissionsGroupBadge } from '../PermissionsGroupBadge';
import { DeletedBadge } from '../DeletedBadge';
import { isEntityDeleted } from '../../../utils/generalUtils';
import { AutoRotateBadge } from '../../Collections/CollectionsPanel/CollectionManager/AutoRotateBadge';
import { SortingType } from '../../Collections/CollectionsPanel/CollectionManager/SortingType';
import { KidsBadge } from '../../Collections/CollectionsPanel/CollectionManager/KidsBadge';
import { collectionLabelTestIds } from '../TestsIds';
import { useCollectionDisplay } from '../../../hooks/Collections/useCollectionDisplay';
import CountryBadge from '../CountryBadge';
import LiveCountriesBadge from '../../Layouts/LiveCountriesBadge';

export const collectionLabelClasses = {
  root: 'collection-label-root',
  title: 'collection-label-title'
};

export interface CollectionLabelProps {
  collectionId: string;
  className?: string;
  onlyName?: boolean;
}

export default function CollectionLabel({
  collectionId,
  className,
  onlyName: nameOnly
}: CollectionLabelProps): JSX.Element {
  const {
    collections: {
      state: { withRecordById },
      hook: { queueIdToFetch }
    }
  } = useData();

  const collection = useRecoilValue(withRecordById(collectionId));
  const isDeleted = isEntityDeleted(collection);
  const { isTop10 } = useCollectionDisplay();

  useEffect(() => {
    if (!collection) {
      queueIdToFetch(collectionId);
    }
  }, [collectionId]);

  if (!collection) return <></>;

  if (nameOnly) {
    return (
      <Localized
        prop={collection.title}
        data-testid={collectionLabelTestIds.title}
        className={collectionLabelClasses.title}
      />
    );
  }

  return (
    <Stack gap={2} direction="row" alignItems="center" className={className}>
      {isDeleted && <DeletedBadge />}
      <Localized
        prop={collection.title}
        data-testid={collectionLabelTestIds.title}
        className={collectionLabelClasses.title}
      />
      {isTop10 && collection.countries?.length && (
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {collection.countries?.map((country) => <CountryBadge key={`prev-top10-${country}`} countryCode={country} />)}
        </div>
      )}
      {collection.status === VersionedDocumentStatus.DRAFT && (
        <DraftBadge data-testid={collectionLabelTestIds.draftBadge} />
      )}
      {collection.genre && (
        <GenreBadge
          label={collection.genre.key}
          data-testid={collectionLabelTestIds.genreBadge}
          forSimilars={collection.forSimilars}
        />
      )}
      {collection.ownerPermissionsGroups && <PermissionsGroupBadge values={collection.ownerPermissionsGroups} />}
      {collection.entitlements && <EntitlementsVisibilityBadge entitlements={collection.entitlements} />}
      {collection.autoRotate && <AutoRotateBadge collection={collection} />}
      {collection.sortingType && <SortingType sortingType={collection.sortingType} />}
      {collection.kids && <KidsBadge collection={collection} />}
      {!!collection.liveCountries?.length && <LiveCountriesBadge countryCodes={collection.liveCountries} />}
    </Stack>
  );
}
